import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../../context/AuthContext";
import { useLeague } from "../../context/LeagueContext";
import { fmtTokens } from "../../lib/format";

const toISO = (d) => d.toISOString().slice(0, 10);

export default function Results() {
  const { currentLeague } = useLeague();
  const [data, setData] = useState(null);
  const [range, setRange] = useState("month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    if (!currentLeague) return;
    const params = new URLSearchParams();
    const today = new Date();
    if (range === "7") {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      params.set("from_date", toISO(from));
      params.set("to_date", toISO(today));
    } else if (range === "month") {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      params.set("from_date", toISO(from));
      params.set("to_date", toISO(today));
    } else if (range === "custom" && fromDate && toDate) {
      params.set("from_date", fromDate);
      params.set("to_date", toDate);
    }
    const qs = params.toString();
    api.get(`/leagues/${currentLeague.id}/results${qs ? `?${qs}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  }, [range, fromDate, toDate, currentLeague]);

  if (!data) {
    return (
      <div className="flex justify-center py-24" data-testid="results-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { totals, players } = data;

  const totalCards = [
    { label: "Satsade tokens", value: fmtTokens(totals.tokens_wagered), testid: "total-tokens-wagered" },
    { label: "Utbetalda tokens", value: fmtTokens(totals.tokens_paid_out), testid: "total-tokens-paid" },
    { label: "Nettoresultat", value: `${totals.net >= 0 ? "+" : ""}${fmtTokens(totals.net)}`, testid: "total-net", color: totals.net >= 0 ? "text-emerald-600" : "text-red-500" },
  ];

  return (
    <div data-testid="admin-results">
      <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-2">Resultat</h1>
      <p className="text-sm text-slate-500 mb-6">Totalöversikt och resultat per spelare i {currentLeague?.name}.</p>

      <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="results-range-filter">
        {[["month", "Denna månad"], ["all", "Alla tider"], ["7", "Senaste veckan"], ["custom", "Välj datum"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setRange(key)}
            data-testid={`range-${key}`}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
              range === key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            {label}
          </button>
        ))}
        {range === "custom" && (
          <div className="flex items-center gap-2 ml-1">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="range-from-input"
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
            <span className="text-sm text-slate-400">–</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="range-to-input"
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
        {totalCards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className="bg-white border border-slate-200 rounded-2xl p-6"
            data-testid={c.testid}
          >
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">{c.label}</p>
            <p className={`font-mono2 font-bold text-2xl ${c.color || ""}`}>{c.value}</p>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 overflow-x-auto" data-testid="results-table-card">
        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-6">Resultat per spelare</p>
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
              <th className="pb-3 font-semibold">Spelare</th>
              <th className="pb-3 font-semibold">Kuponger</th>
              <th className="pb-3 font-semibold">Vunna</th>
              <th className="pb-3 font-semibold">Satsat</th>
              <th className="pb-3 font-semibold">Vunnet</th>
              <th className="pb-3 font-semibold text-right">Netto</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.player_id} className="border-b border-slate-50 last:border-0" data-testid={`player-result-row-${p.name.toLowerCase()}`}>
                <td className="py-3.5 font-display font-semibold">{p.name}</td>
                <td className="py-3.5 font-mono2">{p.coupons_total}</td>
                <td className="py-3.5 font-mono2">{p.coupons_won}</td>
                <td className="py-3.5 font-mono2">{fmtTokens(p.tokens_wagered)}</td>
                <td className="py-3.5 font-mono2">{fmtTokens(p.tokens_won)}</td>
                <td className={`py-3.5 font-mono2 font-bold text-right ${p.net >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {p.net >= 0 ? "+" : ""}{fmtTokens(p.net)}
                </td>
              </tr>
            ))}
            {players.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-slate-500">Inga spelare ännu i ligan.</td></tr>
            )}
          </tbody>
        </table>
      </motion.div>
    </div>
  );
}
